import { Link } from "react-router-dom";
import { Facebook, Mail, MapPin, Phone } from "lucide-react";
import { SITE, SERVICES, CITIES } from "@/data/site";

export default function SiteFooter() {
  return (
    <footer className="bg-slate-950 text-slate-300 relative z-10" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-16 grid gap-12 lg:grid-cols-4">
        <div>
          <div className="flex items-center gap-3">
            <img src={SITE.logo} alt={SITE.name} className="h-14 w-auto bg-white rounded-2xl p-1" />
            <div>
              <p className="font-heading font-extrabold text-white text-lg">{SITE.name}</p>
              <p className="text-xs uppercase tracking-[0.18em] text-amber-400">
                Est. {SITE.established} · {SITE.experience}
              </p>
            </div>
          </div>
          <p className="mt-5 text-sm leading-relaxed text-slate-400">
            Tampa Bay&apos;s trusted pool cleaning, maintenance, and 24/7 emergency service across Pinellas, Hillsborough,
            and Pasco counties.
          </p>
          <div className="mt-6 flex items-center gap-3">
            <a
              href={SITE.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-slate-800 hover:bg-sky-600 inline-flex items-center justify-center transition-colors"
              data-testid="footer-facebook"
              aria-label="Tampa Bay Pool Pros on Facebook"
            >
              <Facebook className="w-4 h-4 text-white" />
            </a>
            <a
              href={`tel:${SITE.phoneTel}`}
              className="w-10 h-10 rounded-full bg-slate-800 hover:bg-amber-400 hover:text-slate-900 inline-flex items-center justify-center transition-colors"
              data-testid="footer-call"
              aria-label="Call Tampa Bay Pool Pros"
            >
              <Phone className="w-4 h-4 text-white" />
            </a>
            <a
              href={`mailto:${SITE.email}`}
              className="w-10 h-10 rounded-full bg-slate-800 hover:bg-emerald-500 inline-flex items-center justify-center transition-colors"
              data-testid="footer-email"
              aria-label="Email Tampa Bay Pool Pros"
            >
              <Mail className="w-4 h-4 text-white" />
            </a>
          </div>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-amber-400 font-bold">Services</p>
          <ul className="mt-4 space-y-2 text-sm">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link
                  to={`/services/${s.slug}`}
                  className="text-slate-300 hover:text-white transition-colors"
                  data-testid={`footer-service-${s.slug}`}
                >
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-amber-400 font-bold">Service Areas</p>
          <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {CITIES.map((c) => (
              <li key={c.slug}>
                <Link
                  to={`/service-areas/${c.slug}`}
                  className="text-slate-300 hover:text-white transition-colors"
                  data-testid={`footer-city-${c.slug}`}
                >
                  {c.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-amber-400 font-bold">Contact</p>
          <ul className="mt-4 space-y-3 text-sm">
            <li className="flex items-start gap-2">
              <Phone className="w-4 h-4 mt-0.5 text-sky-400" />
              <a href={`tel:${SITE.phoneTel}`} className="hover:text-white" data-testid="footer-phone-link">
                {SITE.phoneDisplay}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <Mail className="w-4 h-4 mt-0.5 text-sky-400" />
              <a href={`mailto:${SITE.email}`} className="hover:text-white" data-testid="footer-email-link">
                {SITE.email}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 text-sky-400" />
              <span>Serving the greater Tampa Bay area, Florida</span>
            </li>
          </ul>
          <p className="mt-6 text-xs text-emerald-400 font-semibold">
            24/7 Pool Emergency — call any time.
          </p>
        </div>
      </div>

      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-6 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between text-xs text-slate-500">
          <p>
            © {new Date().getFullYear()} {SITE.name}. Licensed &amp; insured. All rights reserved.
          </p>
          <p>
            <a href={SITE.legacyDomain} className="hover:text-slate-300">
              TampaBayPoolPros.com
            </a>{" "}
            ·{" "}
            <a href={SITE.canonicalDomain} className="hover:text-slate-300">
              TBPoolPros.com
            </a>
          </p>
        </div>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 pb-6 -mt-2 text-center sm:text-right text-xs text-slate-500">
          <p>
            Website Created by{" "}
            <a
              href="https://www.GregMiceli.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-sky-400 hover:text-sky-300 transition-colors"
              data-testid="footer-credit-link"
            >
              www.GregMiceli.com
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
